import { useState, useEffect, useMemo } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import { apiGet, apiPost } from '../shared/api'
import { useI18n } from '../i18n'
import StripePayment from '../components/StripePayment'

declare global {
  interface Window {
    paypal: any
  }
}

interface SubscriptionPlan {
  id: string
  name: string
  price: number
  duration: string
  features: string[]
  popular?: boolean
}

interface UserSubscription {
  planId: string
  expiresAt: string
  status: 'active' | 'expired' | 'cancelled'
}

// Utility function to check if PayPal SDK is ready
function isPayPalReady(): boolean {
  return !!(window.paypal && window.paypal.Buttons && typeof window.paypal.Buttons === 'function')
}

export default function Subscription() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { me } = useOutletContext<{ me: any; setMe: (user: any) => void }>()
  const [userSubscription, setUserSubscription] = useState<UserSubscription | null>(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [paypalReady, setPaypalReady] = useState(false)
  const [paypalCurrency, setPaypalCurrency] = useState('USD')
  const [paypalRenderedFor, setPaypalRenderedFor] = useState<Record<string, boolean>>({})
  const [pricing, setPricing] = useState<{ monthly: number; yearly: number; currency: string } | null>(null)
  const [paymentMethod, setPaymentMethod] = useState<'paypal' | 'stripe'>('paypal')
  
  // Reset PayPal buttons when switching to PayPal payment method
  useEffect(() => {
    if (paymentMethod === 'paypal' && paypalReady) {
      // Clear the rendered tracking to force re-render of PayPal buttons
      setPaypalRenderedFor({})
    }
  }, [paymentMethod, paypalReady])
  
  // Track if component is mounted
  const [isMounted, setIsMounted] = useState(true)

  useEffect(() => {
    setIsMounted(true)
    return () => setIsMounted(false)
  }, [])

  const plans: SubscriptionPlan[] = useMemo(() => {
    const m = pricing?.monthly ?? 1.99
    const y = pricing?.yearly ?? 19.9
    return [
      {
        id: 'free',
        name: t('admin.free'),
        price: 0,
        duration: '3d',
        features: [t('home.features.aggregate'), t('home.features.hls'), t('home.features.simple')]
      },
      {
        id: 'monthly',
        name: t('admin.monthly'),
        price: m,
        duration: '1m',
        features: [t('subscription.unlockMore')],
        popular: true
      },
      {
        id: 'yearly',
        name: t('admin.yearly'),
        price: y,
        duration: '12m',
        features: [t('subscription.unlockMore')]
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricing, t])

  useEffect(() => {
    if (!me) {
      navigate('/login')
      return
    }
    if (isMounted) {
      loadUserSubscription()
    }
    apiGet<{ pricing: { monthly: number; yearly: number; currency: string } }>('/api/public/pricing')
      .then((res) => {
        if (isMounted) {
          setPricing(res.pricing)
        }
      })
      .catch(() => {
        if (isMounted) {
          setPricing({ monthly: 1.99, yearly: 19.9, currency: 'USD' })
        }
      })
    
    // Add a small delay to ensure DOM is ready before loading PayPal SDK
    const timer = setTimeout(() => {
      if (!isMounted) return
      
      let retryCount = 0
      const maxRetries = 3
      
      const attemptPayPalLoad = () => {
        if (!isMounted) return
        ensurePayPalSdk().then((res) => {
          if (!isMounted) return // Don't update state if component unmounted
          if (res) {
            setPaypalCurrency(res.currency)
            setPaypalReady(true)
          } else if (retryCount < maxRetries) {
            retryCount++
            const delay = Math.min(1000 * Math.pow(2, retryCount), 5000) // Exponential backoff, max 5s
            setTimeout(attemptPayPalLoad, delay)
          }
        }).catch((error) => {
          if (!isMounted) return // Don't update state if component unmounted
          console.error('Failed to initialize PayPal SDK:', error)
          if (retryCount < maxRetries) {
            retryCount++
            const delay = Math.min(1000 * Math.pow(2, retryCount), 5000)
            setTimeout(attemptPayPalLoad, delay)
          }
        })
      }
      
      attemptPayPalLoad()
    }, 100)
    
    return () => clearTimeout(timer)
  }, [me, navigate, isMounted])

  useEffect(() => {
    if (!paypalReady || !isMounted) return
    
    // Add a small delay to ensure DOM elements are rendered
    const timer = setTimeout(() => {
      if (!isMounted) return // Check again after delay
      
      // Check if PayPal SDK is actually ready
      if (!isPayPalReady()) {
        console.warn('PayPal SDK not ready, retrying...')
        if (isMounted) {
          setPaypalReady(false)
        }
        return
      }
      
      // Render PayPal buttons for paid plans exactly once each
      plans
        .filter((p) => p.id !== 'free')
        .forEach((plan) => {
          if (!isMounted) return
          if (paypalRenderedFor[plan.id]) return
          const containerId = `paypal-buttons-${plan.id}`
          const container = document.getElementById(containerId)
          if (!container) {
            console.warn(`PayPal container not found: ${containerId}`)
            return
          }
          
          if (!isPayPalReady()) {
            console.warn('PayPal SDK not ready for button rendering')
            return
          }

          // 清除容器内容，确保没有重复的按钮
          container.innerHTML = ''

          try {
            window.paypal
              .Buttons({
                style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' },
                createOrder: async () => {
                  if (!isMounted) return
                  const res = await apiPost<{ orderId: string }>(
                    '/api/paypal/create-order',
                    { planId: plan.id }
                  )
                  return res.orderId
                },
                onApprove: async (data: any) => {
                  if (!isMounted) return
                  try {
                    const orderId: string = data.orderID
                    const res = await apiPost('/api/paypal/capture-order', {
                      orderId,
                      planId: plan.id,
                    })
                    if ((res as any).success) {
                      if (isMounted) {
                        setMessage(t('subscription.subscribeSuccess'))
                      }
                      await loadUserSubscription()
                    } else {
                      if (isMounted) {
                        setMessage((res as any).error || t('subscription.subscribeFailed'))
                      }
                    }
                  } catch (err) {
                    if (isMounted) {
                      setMessage(t('subscription.captureFailed'))
                    }
                  }
                },
                onError: (err: any) => {
                  console.error('PayPal error', err)
                  if (isMounted) {
                    setMessage(t('subscription.paypalError'))
                  }
                },
              })
              .render(`#${containerId}`)
              .then(() => {
                if (isMounted) {
                  setPaypalRenderedFor((prev) => ({ ...prev, [plan.id]: true }))
                }
              })
              .catch((error) => {
                console.error('Failed to render PayPal buttons for plan:', plan.id, error)
              })
          } catch (e) {
            console.error('Failed to render PayPal buttons', e)
          }
        })
    }, 50)
    
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paypalReady, plans, paypalRenderedFor, isMounted])

  const loadUserSubscription = async () => {
    if (!isMounted) return
    try {
      const res = await apiGet('/api/auth/me')
      if (res.user?.subscription && isMounted) {
        setUserSubscription(res.user.subscription)
      }
    } catch (error) {
      console.error('Failed to load subscription:', error)
    }
  }

  const handleSubscribe = async (planId: string) => {
    if (!isMounted) return
    if (planId === 'free') {
      if (isMounted) {
        setMessage('')
      }
      return
    }
    // For paid plans, guide users to PayPal buttons
    const anchor = document.getElementById(`paypal-buttons-${planId}`)
    if (anchor && anchor.scrollIntoView && isMounted) {
      anchor.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const getPlanGradient = (planId: string) => {
    if (planId === 'free') return 'from-emerald-500 via-teal-500 to-cyan-500'
    if (planId === 'monthly') return 'from-blue-500 via-indigo-500 to-purple-500'
    return 'from-fuchsia-500 via-violet-500 to-purple-500'
  }

  const getCurrencySymbol = (code: string) => {
    switch (code) {
      case 'USD': return '$'
      case 'CNY': return '¥'
      case 'EUR': return '€'
      case 'GBP': return '£'
      case 'JPY': return '¥'
      default: return code + ' '
    }
  }

  const getButtonClasses = (planId: string, isPopular?: boolean) => {
    const base = 'w-full py-3 px-6 rounded-xl font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white'
    if (isPopular) {
      return `${base} text-white bg-gradient-to-r ${getPlanGradient(planId)} hover:brightness-110 active:brightness-95 shadow-lg`
    }
    return `${base} bg-white text-gray-900 hover:bg-gray-50 active:bg-gray-100 border border-gray-200`
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{t('subscription.active')}</span>
      case 'expired':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">{t('subscription.expired')}</span>
      case 'cancelled':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{t('subscription.cancelled')}</span>
      default:
        return null
    }
  }

  // Redirect to login if not authenticated
  if (!me) {
    return null // Will redirect to login in useEffect
  }

  return (
    <div className="relative max-w-6xl mx-auto px-4 py-10">
      <div className="pointer-events-none absolute -z-10 inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-gradient-to-r from-blue-400/30 to-purple-400/30 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-gradient-to-r from-emerald-400/30 to-cyan-400/30 blur-3xl" />
      </div>

      <div className="text-center mb-12">
        <h1 className="text-4xl font-extrabold mb-3">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">{t('subscription.choosePlan')}</span>
        </h1>
        <p className="text-lg text-gray-600">{t('subscription.unlockMore')}</p>
      </div>

      {userSubscription && (
        <div className="mb-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
          <h2 className="text-lg font-semibold text-blue-900 mb-2">{t('subscription.currentStatus')}</h2>
          <div className="flex items-center space-x-4">
            <span className="text-blue-700">
              {t('subscription.plan')}：{plans.find(p => p.id === userSubscription.planId)?.name}
            </span>
            {getStatusBadge(userSubscription.status)}
            <span className="text-blue-700">
              {t('subscription.expiresAt')}：{new Date(userSubscription.expiresAt).toLocaleDateString()}
            </span>
          </div>
        </div>
      )}

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.includes('成功') || message.includes('success') ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-8">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className="group relative"
          >
            <div className={`absolute -inset-0.5 rounded-2xl bg-gradient-to-r ${getPlanGradient(plan.id)} opacity-30 blur transition duration-300 group-hover:opacity-70`} />
            <div className="relative rounded-2xl bg-white/80 backdrop-blur shadow-xl border border-white/20 p-8 transition-all duration-300 group-hover:-translate-y-1">
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full text-xs font-semibold shadow-md">
                    {t('subscription.mostPopular')}
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{plan.name}</h3>
                <div className="mb-4 flex items-end justify-center gap-2">
                  <span className={`text-5xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r ${getPlanGradient(plan.id)}`}>
                    {getCurrencySymbol(pricing?.currency || paypalCurrency)}{plan.price.toFixed(2)}
                  </span>
                  <span className="text-gray-600 mb-1">{t('subscription.perDuration', { duration: plan.duration })}</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <svg className="w-5 h-5 text-emerald-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {plan.id === 'free' ? (
                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={loading}
                  className={getButtonClasses(plan.id, plan.popular)}
                >
                  {loading ? t('subscription.processing') : t('subscription.useForFree')}
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => {
                        setPaymentMethod('paypal')
                      }}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                        paymentMethod === 'paypal'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      PayPal
                    </button>
                    <button
                      onClick={() => {
                        // Clear PayPal containers when switching to Stripe
                        plans.filter(p => p.id !== 'free').forEach(plan => {
                          const container = document.getElementById(`paypal-buttons-${plan.id}`)
                          if (container) {
                            container.innerHTML = ''
                          }
                        })
                        setPaymentMethod('stripe')
                      }}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                        paymentMethod === 'stripe'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Stripe
                    </button>
                  </div>
                  
                  {paymentMethod === 'stripe' ? (
                    <StripePayment
                      planId={plan.id}
                      price={plan.price}
                      currency={pricing?.currency || paypalCurrency}
                      onSuccess={async () => {
                        if (isMounted) {
                          setMessage(t('subscription.subscribeSuccess'))
                          await loadUserSubscription()
                        }
                      }}
                      onError={(error) => {
                        if (isMounted) {
                          setMessage(error)
                        }
                      }}
                    />
                  ) : (
                    <>
                      <div className="text-sm text-gray-600 text-center">{t('subscription.usePaypal')}</div>
                      <div 
                        id={`paypal-buttons-${plan.id}`} 
                        className="flex justify-center"
                        key={`paypal-container-${plan.id}`}
                      />
                      {!paypalReady && (
                        <button
                          onClick={() => handleSubscribe(plan.id)}
                          disabled={userSubscription?.planId === plan.id && userSubscription?.status === 'active'}
                          className={getButtonClasses(plan.id, plan.popular)}
                        >
                          {userSubscription?.planId === plan.id && userSubscription?.status === 'active' ? t('subscription.currentPlan') : t('subscription.loadingPayments')}
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 text-center text-gray-600">
        <p>{t('subscription.refundNote')}</p>
        <p className="mt-2">{t('subscription.contactSupport')}</p>
      </div>
    </div>
  )
}

async function loadScriptOnce(src: string): Promise<void> {
  // Check if script already exists
  if (document.querySelector(`script[src="${src}"]`)) {
    return
  }
  
  // Ensure document.body is available
  if (!document.body) {
    // Wait for DOM to be ready
    await new Promise<void>((resolve) => {
      if (document.body) {
        resolve()
      } else {
        const checkBody = () => {
          if (document.body) {
            resolve()
          } else {
            requestAnimationFrame(checkBody)
          }
        }
        checkBody()
      }
    })
  }
  
  await new Promise<void>((resolve, reject) => {
    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('Failed to load script'))
    
    // Double-check body exists before appending
    if (document.body) {
      document.body.appendChild(s)
    } else {
      reject(new Error('Document body not available'))
    }
  })
}

async function ensurePayPalSdk(): Promise<{ currency: string } | null> {
  try {
    const conf = await apiGet<{ clientId: string; currency: string; mode: 'sandbox' | 'live' }>(
      '/api/paypal/config'
    )
    const currency = conf.currency || 'USD'
    const url = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(conf.clientId)}&currency=${encodeURIComponent(currency)}&intent=capture`
    
    try {
      await loadScriptOnce(url)
      return { currency }
    } catch (scriptError) {
      console.error('Failed to load PayPal SDK script:', scriptError)
      return null
    }
  } catch (e) {
    console.error('Failed to get PayPal configuration:', e)
    return null
  }
}
